import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScannerSection, closeCameraAfterScanFlash, type ScannerSectionHandle } from '../scanner';
import { QrPlaceholderIcon } from '../scanner/WebcamPlaceholder';
import { submitShelfIndex, submitShelfIndexBatch, getStorageIdPrefixes, getLabelByStorageIdPrefix, getLabelStatus, searchSkuLocations } from '../../services/shelfIndexAPI';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useShelfValidation } from '../../hooks/useShelfValidation';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import {
  MESSAGE_TONES,
  statusSlotMessageClass,
  cameraMessageClass,
  type MessageTone,
  type ShowMessageOptions,
} from '../../hooks/useStatusMessage';
import ShelfAutocompleteInput from '../shared/ShelfAutocompleteInput';
import ScanQueueChips, { type ScanQueueChip } from '../shared/ScanQueueChips';
import OrderDetailCameraBar, { CameraIcon } from '../orders/orderdetail/OrderDetailCameraBar';
import { SHELF_RANGE, LABEL_TYPES } from '../../constants/app.constants';
import type { LabelType } from '../../constants/app.constants';
import type { ShelfLocation } from '../../types/apiTypes';
import { formatType, areLocationRowsEqual } from '../../utils/shelfIndexUtils';
import {
  SCAN_LABEL_ALLOW_CHECKED_OUT_QUERY,
  SCAN_LABEL_PREFIX_QUERY,
  parseStorageIdPrefixParam,
} from '../../utils/scanLabelHandoff';
import './PrintBoxLabel.css';
import '../orders/orderdetail/pick-list.css';
import '../warehouse/SearchWarehouse.css';

function extractStorageIdPrefix(text: string | null | undefined): string | null {
  if (!text || typeof text !== 'string') return null;
  const cleaned = text.replace(/\s+/g, '').replace(/-/g, '').trim();
  const hexPart = cleaned.match(/^[0-9a-fA-F]+/);
  if (hexPart && hexPart[0].length >= 6) {
    return hexPart[0].substring(0, 6).toLowerCase();
  }
  return null;
}

function areStringArraysEqual(a: string[] = [], b: string[] = []): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

interface LoadedLabelDetails {
  readonly shelf: string;
  readonly labelType: LabelType | '';
  readonly baseQty: string;
  readonly isLongBox: boolean;
}

function shelfKey(value: string): string {
  return value.trim().toUpperCase();
}

/** Empty and "None" are the same unset base qty. Numeric text compares as a number. */
function qtyKey(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'none') return 'none';
  const parsed = Number(trimmed);
  if (!Number.isNaN(parsed)) return String(parsed);
  return trimmed;
}

function itemDetailsChanged(loaded: LoadedLabelDetails, next: LoadedLabelDetails): boolean {
  const nextShelf = shelfKey(next.shelf);
  const shelfChanged = nextShelf.length > 0 && nextShelf !== shelfKey(loaded.shelf);
  return (
    shelfChanged
    || next.labelType !== loaded.labelType
    || qtyKey(next.baseQty) !== qtyKey(loaded.baseQty)
    || next.isLongBox !== loaded.isLongBox
  );
}

const STORAGE_INFO_PREVIEW_COUNT = 5;
const INDEXER_MODES = { ITEM: 'item', SHELF: 'shelf' } as const;
type IndexerMode = typeof INDEXER_MODES[keyof typeof INDEXER_MODES];

/**
 * Lifetime of a success banner. Must match the `pbl-status-success-fade` duration in
 * PrintBoxLabel.css — clearing sooner unmounts the banner before its fade starts, so it
 * would vanish instantly instead of fading out.
 */
const SUCCESS_MESSAGE_MS = 5000;

interface InfoClearButtonProps {
  readonly visible: boolean;
  readonly title: string;
  readonly onClear: () => void;
}

/** Always occupies its slot, so showing or hiding the × never resizes the surrounding row. */
function InfoClearButton({ visible, title, onClear }: InfoClearButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      className={`pbl-info-clear${visible ? '' : ' pbl-info-clear-hidden'}`}
      onClick={(event) => {
        event.stopPropagation();
        onClear();
      }}
      title={title}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      disabled={!visible}
    >
      ×
    </button>
  );
}

interface SectionToggleProps {
  readonly label: string;
  readonly open: boolean;
  /** Renders grayed out and inert (e.g. Storage Information with no storage ID loaded). */
  readonly disabled?: boolean;
  readonly onToggle: () => void;
}

/** Collapsible red section header; grays out and stops responding when disabled. */
function SectionToggle({ label, open, disabled = false, onToggle }: SectionToggleProps): React.ReactElement {
  const expanded = open && !disabled;
  return (
    <div
      className={`pbl-section-title ${disabled ? 'pbl-section-title-disabled' : 'pbl-section-info pbl-section-toggle'}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-expanded={expanded}
      onClick={disabled ? undefined : onToggle}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        onToggle();
      }}
    >
      <span className="pbl-section-text">{label}</span>
      <span className="pbl-section-caret" aria-hidden="true">{expanded ? '▼' : '▶'}</span>
    </div>
  );
}

export interface ShelfIndexerProps {
  /** When set, loads this storage ID on mount (same as scanning the label). */
  readonly initialLoad?: {
    readonly storageIdPrefix: string;
    /** Allow loading when the label is checked out (e.g. re-index from label modal). */
    readonly allowCheckedOut: boolean;
  };
  /** Scrollable compact layout for use inside a full-screen overlay. */
  readonly embedded?: boolean;
}

function ShelfIndexer({ initialLoad, embedded = false }: ShelfIndexerProps) {
  const [searchParams] = useSearchParams();
  const queryLoad = useMemo(() => {
    if (embedded) return undefined;
    const prefix = parseStorageIdPrefixParam(searchParams.get(SCAN_LABEL_PREFIX_QUERY));
    if (!prefix) return undefined;
    return {
      storageIdPrefix: prefix,
      allowCheckedOut: searchParams.get(SCAN_LABEL_ALLOW_CHECKED_OUT_QUERY) === '1',
    };
  }, [embedded, searchParams]);
  const effectiveInitialLoad = initialLoad ?? queryLoad;

  const [storageIdPrefix, setStorageIdPrefix] = useState('');
  const [SKU, setSKU] = useState('');
  /** Shelf the loaded label is currently indexed to; empty means the row has no shelf yet. */
  const [currentShelfIndex, setCurrentShelfIndex] = useState('');
  /** Shelf the user is assigning — the value actually submitted. */
  const [shelfIndex, setShelfIndex] = useState('');
  const [labelType, setLabelType] = useState<LabelType | ''>('');
  const [isLongBox, setIsLongBox] = useState(false);
  /** Details from the loaded label, used to tell whether Index Item has a change to save. */
  const [loadedDetails, setLoadedDetails] = useState<LoadedLabelDetails | null>(null);
  const [isReadyForSubmit, setIsReadyForSubmit] = useState(false);
  const [message, setMessage] = useState('');
  const [errorInvalidInput, setErrorInvalidInput] = useState<string | null>(null);
  const [showManualShelfInput, setShowManualShelfInput] = useState(false);
  const [shelfModalError, setShelfModalError] = useState<string | null>(null);
  const [showManualStorageIdInput, setShowManualStorageIdInput] = useState(false);
  const [manualStorageId, setManualStorageId] = useState('');
  const [storageIdPrefixes, setStorageIdPrefixes] = useState<string[]>([]);
  const [filteredPrefixes, setFilteredPrefixes] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [skuLocations, setSkuLocations] = useState<ShelfLocation[]>([]);
  const [skuLocationsLoading, setSkuLocationsLoading] = useState(false);
  const [storageInfoExpanded, setStorageInfoExpanded] = useState(false);
  /** Storage Information stays collapsed until the user opens it — loading a label never expands it. */
  const [storageInfoOpen, setStorageInfoOpen] = useState(false);
  const [modifyDetailsOpen, setModifyDetailsOpen] = useState(false);
  const [baseQty, setBaseQty] = useState('');
  const [baseQtyError, setBaseQtyError] = useState<string | null>(null);
  const [indexerMode, setIndexerMode] = useState<IndexerMode>(INDEXER_MODES.ITEM);
  const [scanQueue, setScanQueue] = useState<ScanQueueChip[]>([]);
  const [messageTone, setMessageTone] = useState<MessageTone>(MESSAGE_TONES.INFO);
  const [scanSuccessFlashKey, setScanSuccessFlashKey] = useState(0);
  const [webcamOn, setWebcamOn] = useState(false);
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  /** True after we auto-closed the camera for the current ready-to-submit stretch. */
  const autoClosedCameraOnReadyRef = useRef(false);
  const storageIdInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef<boolean>(false);
  const messageClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    loading: shelvesLoading,
    error: shelfLoadError,
    validateShelf,
  } = useShelfValidation();

  const [shelfNumber, setShelfNumber] = useState('');
  const [shelfLetter, setShelfLetter] = useState('');
  const [shelfPosition, setShelfPosition] = useState('');
  const [manualShelfText, setManualShelfText] = useState('');

  const clearMessage = useCallback((): void => {
    if (messageClearTimerRef.current) {
      clearTimeout(messageClearTimerRef.current);
      messageClearTimerRef.current = null;
    }
    setMessage('');
    setMessageTone(MESSAGE_TONES.INFO);
    setErrorInvalidInput(null);
  }, []);

  /** Single entry point for the status line so tone, highlight and auto-clear stay in sync. */
  const showMessage = useCallback((
    text: string,
    tone: MessageTone = MESSAGE_TONES.INFO,
    options?: ShowMessageOptions,
  ): void => {
    if (messageClearTimerRef.current) {
      clearTimeout(messageClearTimerRef.current);
      messageClearTimerRef.current = null;
    }
    setMessage(text);
    setMessageTone(tone);
    setErrorInvalidInput(options?.invalidInput ?? null);
    if (tone === MESSAGE_TONES.SUCCESS) {
      setScanSuccessFlashKey((key) => key + 1);
    }
    // Success always lives exactly as long as its fade animation; callers cannot cut it short.
    const autoClearMs =
      tone === MESSAGE_TONES.SUCCESS ? SUCCESS_MESSAGE_MS : options?.autoClearMs;
    if (autoClearMs != null) {
      messageClearTimerRef.current = setTimeout(() => {
        messageClearTimerRef.current = null;
        setMessage('');
        setMessageTone(MESSAGE_TONES.INFO);
        setErrorInvalidInput(null);
      }, autoClearMs);
    }
  }, []);

  useEffect(() => () => {
    if (messageClearTimerRef.current) {
      clearTimeout(messageClearTimerRef.current);
      messageClearTimerRef.current = null;
    }
  }, []);

  /** `quiet` background refreshes stay silent; the initial load surfaces failures. */
  const refreshStoragePrefixes = useCallback(async (quiet = true) => {
    try {
      const prefixes = await getStorageIdPrefixes();
      setStorageIdPrefixes((prev) => (areStringArraysEqual(prev, prefixes) ? prev : prefixes));
    } catch (error: unknown) {
      console.error('Error loading storage ID prefixes:', error);
      if (!quiet) {
        showMessage('Error loading storage IDs. Please refresh.', MESSAGE_TONES.ERROR);
      }
    }
  }, [showMessage]);

  useEffect(() => {
    refreshStoragePrefixes(false);
  }, [refreshStoragePrefixes]);

  useEffect(() => {
    if (shelfLoadError) {
      console.error('Error loading shelf names:', shelfLoadError);
    }
  }, [shelfLoadError]);

  useEffect(() => {
    const shelfReady = shelfIndex.length > 0;
    const resolvedShelf = shelfIndex.trim() || loadedDetails?.shelf.trim() || '';
    const detailChanged = loadedDetails != null && itemDetailsChanged(loadedDetails, {
      shelf: shelfIndex,
      labelType,
      baseQty,
      isLongBox,
    });
    const itemReady =
      storageIdPrefix.length === 6 && SKU.length > 0 && resolvedShelf.length > 0 && detailChanged;
    const shelfModeReady = shelfReady && scanQueue.length > 0;
    const ready = indexerMode === INDEXER_MODES.SHELF ? shelfModeReady : itemReady;
    setIsReadyForSubmit(ready);
    // Index Item: close once so the form and Submit are visible. Index Shelf keeps
    // the camera open so the user can keep scanning chips until they tap Submit.
    let closeTimer: ReturnType<typeof setTimeout> | null = null;
    if (indexerMode === INDEXER_MODES.ITEM && ready) {
      if (!autoClosedCameraOnReadyRef.current) {
        closeTimer = closeCameraAfterScanFlash(() => {
          autoClosedCameraOnReadyRef.current = true;
          setWebcamOn(false);
        });
      }
    } else {
      autoClosedCameraOnReadyRef.current = false;
    }
    return () => {
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [storageIdPrefix, SKU, shelfIndex, labelType, baseQty, isLongBox, loadedDetails, indexerMode, scanQueue]);

  useEffect(() => {
    if (skipAutocompleteOpenRef.current) {
      skipAutocompleteOpenRef.current = false;
      if (manualStorageId.length > 0) {
        const searchTerm = manualStorageId.toLowerCase().trim();
        const filtered = storageIdPrefixes
          .filter((p) => p.toLowerCase().includes(searchTerm))
          .slice(0, 10);
        setFilteredPrefixes(filtered);
      }
      setSelectedIndex(-1);
      setShowAutocomplete(false);
      return;
    }
    if (manualStorageId.length > 0) {
      const searchTerm = manualStorageId.toLowerCase().trim();
      const filtered = storageIdPrefixes
        .filter((p) => p.toLowerCase().includes(searchTerm))
        .slice(0, 10);
      setFilteredPrefixes(filtered);
      setShowAutocomplete(filtered.length > 0);
    } else {
      setFilteredPrefixes([]);
      setShowAutocomplete(false);
    }
    setSelectedIndex(-1);
  }, [manualStorageId, storageIdPrefixes]);

  useEffect(() => {
    if (showAutocomplete && storageIdInputRef.current && autocompleteRef.current) {
      const inputRect = storageIdInputRef.current.getBoundingClientRect();
      const dropdown = autocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showAutocomplete, manualStorageId, filteredPrefixes]);

  useClickOutside(
    [autocompleteRef, storageIdInputRef],
    () => setShowAutocomplete(false),
  );

  const refreshSkuLocations = useCallback(async (quiet = false) => {
    if (!SKU) {
      setSkuLocations([]);
      setSkuLocationsLoading(false);
      return;
    }
    const showLoading = !quiet;
    if (showLoading) {
      setSkuLocationsLoading(true);
    }
    try {
      const result = await searchSkuLocations(SKU);
      const nextLocations = result.locations || [];
      setSkuLocations((prev) => (areLocationRowsEqual(prev, nextLocations) ? prev : nextLocations));
    } catch (error: unknown) {
      console.error('Error loading SKU locations:', error);
      setSkuLocations([]);
    } finally {
      if (showLoading) {
        setSkuLocationsLoading(false);
      }
    }
  }, [SKU]);

  useEffect(() => {
    refreshSkuLocations(false);
  }, [refreshSkuLocations]);

  const handleHybridSync = useCallback(async () => {
    await refreshStoragePrefixes(true);
    if (SKU) {
      await refreshSkuLocations(true);
    }
  }, [SKU, refreshStoragePrefixes, refreshSkuLocations]);

  useHybridShelfIndexSync({
    enabled: true,
    onSync: handleHybridSync,
    pollMs: 20000,
  });

  useEffect(() => {
    setStorageInfoExpanded(false);
  }, [SKU]);

  const validateShelfIndex = (shelf: string): boolean => {
    if (shelvesLoading) return false;
    return validateShelf(shelf);
  };

  const matchShelfIndex = (ocrText: string): string | null => {
    const shelfPattern = /\d{1,2}-[A-Za-z]-\d+/;
    const shelfMatch = ocrText.match(shelfPattern);
    if (shelfMatch) {
      return shelfMatch[0];
    }
    return null;
  };

  const handleIndexerModeChange = (next: IndexerMode): void => {
    if (next === indexerMode) return;
    setIndexerMode(next);
    setLabelType('');
    setIsLongBox(false);
    setScanQueue([]);
    clearMessage();
    setBaseQty('');
    setBaseQtyError(null);
    setLoadedDetails(null);
    if (next === INDEXER_MODES.SHELF) {
      setStorageIdPrefix('');
      setSKU('');
      setCurrentShelfIndex('');
      setSkuLocations([]);
      setShowManualStorageIdInput(false);
    }
  };

  const addScannedPrefix = async (prefix: string): Promise<boolean> => {
    const normalized = prefix.trim().toLowerCase();
    if (normalized.length !== 6) {
      const badValue = normalized || '(empty)';
      showMessage(`Storage ID ${badValue} must be 6 characters`, MESSAGE_TONES.ERROR, {
        invalidInput: badValue,
      });
      return false;
    }
    if (scanQueue.some((item) => item.prefix === normalized)) {
      showMessage(`${normalized} already scanned.`, MESSAGE_TONES.ERROR);
      return false;
    }
    const [status, label] = await Promise.all([
      getLabelStatus(normalized),
      getLabelByStorageIdPrefix(normalized),
    ]);
    if (!status?.found) {
      showMessage(`Label not found in DB for storage ID ${normalized}`, MESSAGE_TONES.ERROR, {
        invalidInput: normalized,
      });
      return false;
    }
    if (status.checkedIn === false) {
      showMessage(`Label is checked out of warehouse: ${normalized}`, MESSAGE_TONES.ERROR, {
        invalidInput: normalized,
      });
      return false;
    }
    const sku = label?.sku?.trim() ?? '';
    const queued: ScanQueueChip = sku ? { prefix: normalized, sku } : { prefix: normalized };
    setScanQueue((prev) => (prev.some((item) => item.prefix === normalized) ? prev : [...prev, queued]));
    showMessage(sku ? `✓ Added ${normalized} ${sku}` : `✓ Added ${normalized}`, MESSAGE_TONES.SUCCESS);
    return true;
  };

  /** QR scans arrive as decoded text; text scans arrive as an image and are OCR'd first. */
  const handleCapture = async (text: string | null): Promise<boolean> => {
    if (!text || !text.trim()) {
      showMessage('Error processing image. Please try again.', MESSAGE_TONES.ERROR);
      return false;
    }

    const prefix = extractStorageIdPrefix(text);
    if (prefix) {
      if (indexerMode === INDEXER_MODES.SHELF) {
        return addScannedPrefix(prefix);
      }
      const applied = await applyStorageIdPrefix(prefix);
      if (applied) {
        showMessage(`Matched storage ID ${prefix}.`, MESSAGE_TONES.SUCCESS);
      }
      return applied;
    }

    const ocrText = cleanText(text);
    const matchedShelfIndex = matchShelfIndex(ocrText);

    if (matchedShelfIndex) {
      const normalizedShelf = matchedShelfIndex.toUpperCase().trim();
      if (validateShelfIndex(normalizedShelf)) {
        setShelfIndex(normalizedShelf);
        showMessage(`Matched shelf index ${normalizedShelf}.`, MESSAGE_TONES.SUCCESS);
        return true;
      }
      showMessage(`Shelf ${normalizedShelf} does not exist`, MESSAGE_TONES.ERROR, {
        invalidInput: normalizedShelf,
      });
      return false;
    }
    showMessage(`Could not match shelf. Scan input: ${ocrText}`, MESSAGE_TONES.ERROR, {
      invalidInput: ocrText,
    });
    return false;
  };

  const cleanText = (ocrText: string): string => {
    const removeSpace = ocrText.replace(/\s+/g, '').toUpperCase();
    const transform = transformString1toI(removeSpace);
    return transform;
  };

  function transformString1toI(input: string): string {
    const regex = /^(\d+)-1-(\d+)$/;
    const match = input.match(regex);
    if (match) {
      return `${match[1]}-I-${match[2]}`;
    } else {
      return input;
    }
  }

  const handleManualShelfInput = () => {
    let normalizedIndex: string;

    if (manualShelfText.trim()) {
      normalizedIndex = manualShelfText.trim().toUpperCase();
    } else if (shelfNumber && shelfLetter && shelfPosition) {
      normalizedIndex = `${shelfNumber}-${shelfLetter}-${shelfPosition}`.toUpperCase();
    } else {
      setShowManualShelfInput(false);
      showMessage('Please select all three fields or enter a shelf name.', MESSAGE_TONES.ERROR);
      return;
    }

    if (!validateShelfIndex(normalizedIndex)) {
      setShelfModalError(`Shelf ${normalizedIndex} does not exist`);
      showMessage(`Shelf ${normalizedIndex} does not exist`, MESSAGE_TONES.ERROR, {
        invalidInput: normalizedIndex,
      });
      return;
    }

    setShelfModalError(null);
    setShelfIndex(normalizedIndex);
    setShowManualShelfInput(false);

    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');

    showMessage(`Shelf index ${normalizedIndex} set successfully.`, MESSAGE_TONES.SUCCESS);
  };

  const handleManualStorageIdInputClose = () => {
    setShowManualStorageIdInput(false);
    setManualStorageId('');
  };

  const handleManualShelfInputClose = () => {
    setShowManualShelfInput(false);
    setShelfModalError(null);
    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');
  };

  const handleManualStorageIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.substring(0, 6);
    setManualStorageId(value.toLowerCase());
  };

  const handleManualStorageIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || filteredPrefixes.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredPrefixes.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectStorageIdPrefix(filteredPrefixes[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const applyStorageIdPrefix = useCallback(async (
    prefix: string,
    options?: { readonly allowCheckedOut?: boolean },
  ): Promise<boolean> => {
    const normalizedPrefix = String(prefix || '').trim().toLowerCase();
    if (normalizedPrefix.length !== 6) {
      const badValue = normalizedPrefix || String(prefix || '').trim() || '(empty)';
      showMessage(`Storage ID ${badValue} must be 6 characters`, MESSAGE_TONES.ERROR, {
        invalidInput: badValue,
      });
      return false;
    }

    const status = await getLabelStatus(normalizedPrefix);
    if (!status?.found) {
      showMessage(`Label not found in DB for storage ID ${normalizedPrefix}`, MESSAGE_TONES.ERROR, {
        invalidInput: normalizedPrefix,
      });
      return false;
    }
    if (status.checkedIn === false && !options?.allowCheckedOut) {
      showMessage(`Label is checked out of warehouse: ${normalizedPrefix}`, MESSAGE_TONES.ERROR, {
        invalidInput: normalizedPrefix,
      });
      return false;
    }

    const label = await getLabelByStorageIdPrefix(normalizedPrefix);
    if (label && label.sku) {
      setStorageIdPrefix(normalizedPrefix);
      setSKU(label.sku);
      // Existing shelf only fills Current Shelf. New Shelf stays empty unless the user sets one.
      const loadedShelf = label.shelfIndex ?? '';
      const loadedType: LabelType | '' =
        label.type === 'stock' || label.type === 'pick bin' ? label.type : '';
      const loadedQty = label.baseQty != null ? String(label.baseQty) : 'None';
      const loadedLong = label.isLongBox === true;
      setCurrentShelfIndex(loadedShelf);
      setLabelType(loadedType);
      setBaseQty(loadedQty);
      setBaseQtyError(null);
      setIsLongBox(loadedLong);
      setLoadedDetails({
        shelf: loadedShelf,
        labelType: loadedType,
        baseQty: loadedQty,
        isLongBox: loadedLong,
      });
      setShowManualStorageIdInput(false);
      setManualStorageId('');
      showMessage(`Storage ID ${normalizedPrefix} set.`, MESSAGE_TONES.SUCCESS);
      return true;
    }

    showMessage(`Label not found in DB for storage ID ${normalizedPrefix}`, MESSAGE_TONES.ERROR, {
      invalidInput: normalizedPrefix,
    });
    return false;
  }, [showMessage]);

  const initialLoadAppliedRef = useRef(false);
  useEffect(() => {
    if (!effectiveInitialLoad || initialLoadAppliedRef.current) return;
    initialLoadAppliedRef.current = true;
    void applyStorageIdPrefix(effectiveInitialLoad.storageIdPrefix, {
      allowCheckedOut: effectiveInitialLoad.allowCheckedOut,
    });
  }, [effectiveInitialLoad, applyStorageIdPrefix]);

  const selectStorageIdPrefix = async (prefix: string) => {
    skipAutocompleteOpenRef.current = true;
    setManualStorageId(prefix);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setShowManualStorageIdInput(false);
    await applyStorageIdPrefix(prefix);
  };

  const handleManualStorageIdSubmit = async () => {
    const value = manualStorageId.trim().toLowerCase();
    setShowManualStorageIdInput(false);
    await applyStorageIdPrefix(value);
  };

  const handleSubmit = async () => {
    const trimmedQty = baseQty.trim();
    if (trimmedQty !== '' && trimmedQty !== 'None' && isNaN(Number(trimmedQty))) {
      setBaseQtyError('Base Qty must be a number or None');
      return;
    }
    setBaseQtyError(null);
    showMessage('Submitting...');

    let resolvedBaseQty: number | null | undefined;
    if (trimmedQty === 'None' || trimmedQty === '') {
      resolvedBaseQty = null;
    } else {
      resolvedBaseQty = Number(trimmedQty);
    }

    const assignedShelf = shelfIndex.trim() || currentShelfIndex.trim();
    const dataToSubmit = {
      shelfIndex: assignedShelf,
      SKU,
      storageIdPrefix: storageIdPrefix || undefined,
      labelType: labelType || undefined,
      baseQty: resolvedBaseQty,
      isLongBox,
    };

    if (indexerMode === INDEXER_MODES.SHELF) {
      const enteredBaseQty =
        trimmedQty !== '' && trimmedQty !== 'None' && !isNaN(Number(trimmedQty));
      try {
        const result = await submitShelfIndexBatch({
          shelfIndex,
          storageIdPrefixes: scanQueue.map((item) => item.prefix),
          labelType: labelType || undefined,
          baseQty: enteredBaseQty ? Number(trimmedQty) : undefined,
          isLongBox,
        });
        clearMessage();
        const failedPrefixes = new Set(result.failed.map((f) => f.prefix).filter(Boolean));
        if (result.assigned > 0) {
          await refreshStoragePrefixes(true);
          setShowSuccess(true);
          setScanQueue((prev) => prev.filter((item) => failedPrefixes.has(item.prefix)));
          setLabelType('');
          setIsLongBox(false);
          setBaseQty('');
          setBaseQtyError(null);
          setTimeout(() => setShowSuccess(false), 1000);
        }
        if (result.failed.length > 0) {
          showMessage(
            result.failed.map((f) => (f.prefix ? `${f.prefix}: ${f.message}` : f.message)).join('; '),
            MESSAGE_TONES.ERROR,
          );
        }
      } catch (error: unknown) {
        showMessage('Error submitting shelf index. Please try again.', MESSAGE_TONES.ERROR);
      }
      return;
    }

    try {
      const result = await submitShelfIndex(dataToSubmit);

      clearMessage();

      if (result && typeof result === 'string' && result.toLowerCase().includes('success')) {
        await Promise.all([
          refreshStoragePrefixes(true),
          refreshSkuLocations(),
        ]);
        setShowSuccess(true);
        setStorageIdPrefix('');
        setSKU('');
        setCurrentShelfIndex('');
        setShelfIndex('');
        setLabelType('');
        setIsLongBox(false);
        setBaseQty('');
        setBaseQtyError(null);
        setLoadedDetails(null);
        setTimeout(() => setShowSuccess(false), 1000);
      } else {
        showMessage(result || 'Failed to submit shelf index.', MESSAGE_TONES.ERROR);
      }
    } catch (error: unknown) {
      showMessage('Error submitting shelf index. Please try again.', MESSAGE_TONES.ERROR);
    }
  };

  const numbers1to10 = SHELF_RANGE.NUMBERS_1_TO_10;
  const lettersAtoZ = SHELF_RANGE.LETTERS_A_TO_Z;
  const numbers1to6 = SHELF_RANGE.POSITIONS_1_TO_6;
  const isItemMode = indexerMode === INDEXER_MODES.ITEM;
  /** Scan / manual-entry control for the shelf being assigned — shared by both mode layouts. */
  const renderShelfControl = (): React.ReactElement => (
    <div className="pbl-info-value-wrapper">
      <span
        className={`pbl-info-value pbl-info-clickable pbl-shelf-value${
          shelfIndex ? ' pbl-shelf-value-set' : ' pbl-info-empty'
        }`}
        onClick={() => {
          setShowManualShelfInput(true);
          setShowManualStorageIdInput(false);
          if (shelfIndex) {
            const parts = shelfIndex.split('-');
            if (parts.length === 3) {
              setShelfNumber(parts[0]);
              setShelfLetter(parts[1]);
              setShelfPosition(parts[2]);
            }
          }
        }}
      >
        {shelfIndex || 'Scan or enter manually'}
      </span>
      <InfoClearButton
        visible={shelfIndex.length > 0}
        title="Clear Shelf"
        onClear={() => {
          setShelfIndex('');
          showMessage('Shelf cleared', MESSAGE_TONES.INFO, { autoClearMs: 2000 });
        }}
      />
    </div>
  );
  /** Storage Information only has something to show once a storage ID (and its SKU) is loaded. */
  const storageInfoAvailable = storageIdPrefix.length > 0 && SKU.length > 0;
  const hasMoreStorageInfo = skuLocations.length > STORAGE_INFO_PREVIEW_COUNT;
  const storageInfoCollapsed = hasMoreStorageInfo && !storageInfoExpanded;
  const visibleSkuLocations = storageInfoCollapsed
    ? skuLocations.slice(0, STORAGE_INFO_PREVIEW_COUNT + 1)
    : skuLocations;
  const isSuccessMessage = messageTone === MESSAGE_TONES.SUCCESS;
  const messageBody = errorInvalidInput && message.includes(errorInvalidInput) ? (
    <>
      {message.split(errorInvalidInput)[0]}
      <span className="pbl-message-invalid">{errorInvalidInput}</span>
      {message.split(errorInvalidInput).slice(1).join(errorInvalidInput)}
    </>
  ) : (
    message
  );
  const cameraMessageOverlay = message ? (
    <div
      key={isSuccessMessage ? `success-${scanSuccessFlashKey}` : 'status'}
      className={cameraMessageClass(messageTone)}
      role="status"
    >
      {messageBody}
    </div>
  ) : null;

  return (
    <div
      className={`pbl-container pbl-shelf-indexer${embedded ? ' pbl-container-embedded' : ''}${webcamOn ? ' pbl-camera-open' : ''}`}
    >
      {showSuccess && (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      )}

      <header className="pbl-page-header">
        {embedded ? <h1 className="pbl-title">Shelf Indexer</h1> : null}
        <div className="pbl-mode-toggle" role="group" aria-label="Indexer mode">
          <button
            type="button"
            className={`pbl-mode-toggle-btn${indexerMode === INDEXER_MODES.ITEM ? ' pbl-mode-toggle-btn-active' : ''}`}
            aria-pressed={indexerMode === INDEXER_MODES.ITEM}
            onClick={() => handleIndexerModeChange(INDEXER_MODES.ITEM)}
          >
            Index Item
          </button>
          <button
            type="button"
            className={`pbl-mode-toggle-btn${indexerMode === INDEXER_MODES.SHELF ? ' pbl-mode-toggle-btn-active' : ''}`}
            aria-pressed={indexerMode === INDEXER_MODES.SHELF}
            onClick={() => handleIndexerModeChange(INDEXER_MODES.SHELF)}
          >
            Index Shelf
          </button>
        </div>
        {/* Camera open: status rides on the preview (see cameraMessageOverlay). Closed: this
            reserved band sits between the mode buttons and the first section header. */}
        <div className="pbl-page-header-status" role="status" aria-live="polite">
          {message && !webcamOn ? (
            <div
              key={isSuccessMessage ? `success-${scanSuccessFlashKey}` : 'status'}
              className={statusSlotMessageClass(messageTone)}
            >
              {messageBody}
            </div>
          ) : null}
        </div>
      </header>

      <div className="pbl-page-body">
      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">
            {indexerMode === INDEXER_MODES.SHELF ? 'Shelf Index' : 'New Item Index'}
          </span>
        </div>
        <div className="pbl-info-display">
          {isItemMode ? (
            <div className="pbl-info-row-pair">
              {/* Loaded label: storage ID and the shelf it is on today, side by side */}
              <div className="pbl-info-row">
                <span className="pbl-info-label">Storage ID:</span>
                <div className="pbl-info-value-wrapper">
                  <span
                    className={`pbl-info-value pbl-info-clickable ${!storageIdPrefix ? 'pbl-info-empty' : ''}`}
                    onClick={() => {
                      setShowManualStorageIdInput(true);
                      setShowManualShelfInput(false);
                      setManualStorageId(storageIdPrefix || '');
                      setTimeout(() => {
                        const modal = document.querySelector('.pbl-modal-overlay');
                        if (modal) modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        if (storageIdInputRef.current) {
                          storageIdInputRef.current.focus();
                          const len = storageIdInputRef.current.value.length;
                          storageIdInputRef.current.setSelectionRange(len, len);
                        }
                      }, 100);
                    }}
                  >
                    {storageIdPrefix || 'Enter'}
                  </span>
                  <InfoClearButton
                    visible={storageIdPrefix.length > 0}
                    title="Clear Storage ID"
                    onClear={() => {
                      setStorageIdPrefix('');
                      setSKU('');
                      setCurrentShelfIndex('');
                      setShelfIndex('');
                      setLabelType('');
                      setIsLongBox(false);
                      setBaseQty('');
                      setBaseQtyError(null);
                      setLoadedDetails(null);
                      showMessage('Storage ID cleared', MESSAGE_TONES.WARNING, { autoClearMs: 2000 });
                    }}
                  />
                </div>
              </div>
              <div className="pbl-info-row">
                <span className="pbl-info-label">Shelf:</span>
                <span className={`pbl-info-value${currentShelfIndex ? '' : ' pbl-info-empty'}`}>
                  {currentShelfIndex || 'None'}
                </span>
              </div>
            </div>
          ) : null}
          {/* Same row in both modes so the empty-state type size does not jump on switch. */}
          <div className="pbl-info-row pbl-shelf-assign-row">
            <span className="pbl-info-label">{isItemMode ? 'New Shelf:' : 'Shelf:'}</span>
            {renderShelfControl()}
          </div>
        </div>

        {indexerMode === INDEXER_MODES.SHELF ? (
          <div className="pbl-scan-repo">
            <div className="pbl-scan-repo-notice">Scan all items on the shelf</div>
            <ScanQueueChips
              items={scanQueue}
              onRemove={(prefix) => setScanQueue((prev) => prev.filter((item) => item.prefix !== prefix))}
            />
          </div>
        ) : null}
      </div>

      <div className="pbl-section">
        <SectionToggle
          label="Modify Details (Optional)"
          open={modifyDetailsOpen}
          onToggle={() => setModifyDetailsOpen((prev) => !prev)}
        />
        {modifyDetailsOpen && (
          <div className="pbl-info-display">
            <div className="pbl-info-row">
              <span className="pbl-info-label">Label Type:</span>
              <div className="pbl-info-value-wrapper">
                <select
                  className="pbl-dropdown"
                  value={labelType}
                  // Cast needed: <select>.value is always string; options are constrained to LABEL_TYPES.
                  onChange={(e) => setLabelType(e.target.value as LabelType)}
                  style={{ minWidth: '120px' }}
                >
                  {labelType === '' && (
                    <option value="" disabled hidden>---</option>
                  )}
                  {LABEL_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type === 'pick bin' ? 'Pick Bin' : 'Stock'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pbl-info-row">
              <span className="pbl-info-label">Change Base Qty:</span>
              <div className="pbl-info-value-wrapper">
                <input
                  type="text"
                  value={baseQty}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBaseQty(val);
                    if (val === 'None' || val === '' || !isNaN(Number(val))) {
                      setBaseQtyError(null);
                    }
                  }}
                  onFocus={() => { if (baseQty === 'None') setBaseQty(''); }}
                  onBlur={() => {
                    if (indexerMode === INDEXER_MODES.ITEM && baseQty.trim() === '') setBaseQty('None');
                  }}
                  placeholder="None"
                  className="pbl-base-qty-input"
                  style={{ minWidth: '120px' }}
                />
                {baseQtyError && (
                  <span style={{ color: '#ff6b6b', fontSize: '12px', marginLeft: '8px' }}>{baseQtyError}</span>
                )}
              </div>
            </div>
            <div className="pbl-info-row">
              <span className="pbl-info-label">Long Box:</span>
              <div className="pbl-info-value-wrapper">
                <button
                  type="button"
                  className="pbl-long-box-check"
                  aria-pressed={isLongBox}
                  aria-label="Long Box"
                  onClick={() => setIsLongBox((prev) => !prev)}
                >
                  {isLongBox ? '✓' : ''}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isItemMode && (
        <div className="pbl-section">
          <SectionToggle
            label="Storage Information"
            open={storageInfoOpen}
            disabled={!storageInfoAvailable}
            onToggle={() => setStorageInfoOpen((prev) => !prev)}
          />
          {storageInfoAvailable && storageInfoOpen && (
            <div className="sw-results-section pbl-storage-info-section">
              {skuLocationsLoading ? (
                <div className="sw-results-empty">Loading...</div>
              ) : skuLocations.length === 0 ? (
                <div className="sw-results-empty">No locations found for this SKU</div>
              ) : (
                <div className="sw-table-scroll">
                  <div className={storageInfoCollapsed ? 'pbl-storage-info-clip' : undefined}>
                    <table className="sw-results-table sw-results-type-col-2">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Type</th>
                          <th>Shelf</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleSkuLocations.map((loc, idx) => {
                          const isLoadedRow = extractStorageIdPrefix(loc.id) === storageIdPrefix;
                          return (
                            <tr
                              key={`${loc.shelf_index}-${loc.id}-${idx}`}
                              className={isLoadedRow ? 'pbl-results-row-loaded' : undefined}
                            >
                              <td>{SKU}</td>
                              <td className="sw-results-type-cell">{formatType(loc.type)}</td>
                              <td>{loc.shelf_index}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {hasMoreStorageInfo && (
                    <button
                      type="button"
                      className={`pbl-storage-info-more${storageInfoExpanded ? ' pbl-storage-info-more-expanded' : ''}`}
                      onClick={() => setStorageInfoExpanded((prev) => !prev)}
                      aria-expanded={storageInfoExpanded}
                      aria-label={storageInfoExpanded ? 'Show fewer storage locations' : 'Show more storage locations'}
                    >
                      <svg viewBox="0 0 24 24" width="40" height="40" focusable="false" aria-hidden="true">
                        <path
                          d="M6 9 L12 15 L18 9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showManualShelfInput && (
        <div className="pbl-modal-overlay" onClick={handleManualShelfInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="pbl-modal-close"
              onClick={handleManualShelfInputClose}
            >
              ×
            </button>
            <div className="pbl-modal-title">Enter Shelf Index</div>
            <div className="pbl-manual-popup">
              <div className="pbl-dropdown-container">
                <select
                  value={shelfNumber}
                  onChange={(e) => { setShelfNumber(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {numbers1to10.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="pbl-separator">-</span>
                <select
                  value={shelfLetter}
                  onChange={(e) => { setShelfLetter(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {lettersAtoZ.map(letter => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>
                <span className="pbl-separator">-</span>
                <select
                  value={shelfPosition}
                  onChange={(e) => { setShelfPosition(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {numbers1to6.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              <div className="pbl-or-divider">or</div>
              <ShelfAutocompleteInput
                value={manualShelfText}
                onChange={(val) => { setManualShelfText(val); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                onSelect={(shelf) => { setManualShelfText(shelf); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                placeholder="Type shelf name"
              />
              {shelfModalError && (
                <div className="pbl-message" style={{ color: '#ff6b6b' }}>{shelfModalError}</div>
              )}
              <button
                onClick={handleManualShelfInput}
                className="pbl-submit-button"
                disabled={(!shelfNumber || !shelfLetter || !shelfPosition) && !manualShelfText.trim()}
              >
                Set Shelf Index
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualStorageIdInput && (
        <div className="pbl-modal-overlay" onClick={handleManualStorageIdInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pbl-modal-close" onClick={handleManualStorageIdInputClose}>×</button>
            <div className="pbl-modal-title">Enter Storage ID (first 6 chars)</div>
            <div className="pbl-manual-popup">
              <div className="pbl-sku-input-wrapper">
                <input
                  ref={storageIdInputRef}
                  type="text"
                  value={manualStorageId}
                  onChange={handleManualStorageIdChange}
                  onKeyDown={handleManualStorageIdKeyDown}
                  placeholder="e.g. a1b2c3"
                  className="pbl-sku-input"
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && selectedIndex < 0) handleManualStorageIdSubmit();
                  }}
                />
                {showAutocomplete && filteredPrefixes.length > 0 && (
                  <div ref={autocompleteRef} className="pbl-autocomplete-dropdown">
                    {filteredPrefixes.map((prefix, index) => (
                      <div
                        key={prefix}
                        className={`pbl-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                        onClick={() => selectStorageIdPrefix(prefix)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        {prefix}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleManualStorageIdSubmit}
                className="pbl-submit-button"
                disabled={manualStorageId.trim().length !== 6}
              >
                Set Storage ID
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pbl-submit-section">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isReadyForSubmit}
          className="pbl-submit-button pbl-submit-button-main"
        >
          Submit
        </button>
      </div>
      </div>

      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        leftActions={
          <div className="order-detail-bottom-bar-left-group">
            <div
              className="order-detail-scan-mode-toggle pbl-scan-mode-both-on"
              role="status"
              title="QR and text scan are both on"
              aria-label="Scan mode: QR and text. Both on."
            >
              <span className="order-detail-scan-mode-option active">
                <QrPlaceholderIcon />
              </span>
              <span className="order-detail-scan-mode-option active">
                <span className="placeholder-text-chip">a..z</span>
              </span>
            </div>
          </div>
        }
        centerActions={
          <button
            type="button"
            className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
            onClick={() => setWebcamOn(true)}
            aria-label="Open camera"
          >
            <CameraIcon size={28} />
          </button>
        }
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode="hybrid"
          onCapture={handleCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container pbl-shelf-indexer-scanner"
          overlay={cameraMessageOverlay}
          cornerAction={
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          }
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default ShelfIndexer;
